import React, { useEffect, useState, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { GraduationCap, Play, Clock, BookOpen, Headphones, Mic, PenTool, Pause, CheckCircle, ClipboardList, Calendar, Volume2 } from 'lucide-react';
import axios from 'axios';
import AuthContext from '@/contexts/AuthContext';
import { ProficiencyLevelRings, ProficiencyCompositeBar } from '@/components/ProficiencyLevelRings';
import { getCanDo, getLevelName, getWidaColor, scoreToWidaLevel } from '@/utils/proficiencyHelpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MyAssessment = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [pausedExams, setPausedExams] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [myLevels, setMyLevels] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    // v6.36.10 — Removed redundant `if (!user || user.role !== 'student')
    // navigate('/')` defensive guard. ProtectedRoute already enforces
    // allowedRoles=['student'] at the route layer. The previous guard
    // could fire during a transient `user=null` window after a hard
    // reload (window.location.href = '/my-assessment') and would
    // navigate the freshly-minted demo student back to Home before
    // AuthContext finished hydrating. This was the root cause of the
    // "Student Dashboard (Demo) lands on home landing page" bug
    // reported by operator on 2026-06-01.

    // v6.36.16 — Play MOI mascot greeting once per student login.
    // Flag set by AuthContext.login / loginWithEmail / UserMenu demo
    // quick-switch. Cleared after first play so refreshes within the
    // same session don't replay. Failures are silent (no toast).
    const playGreeting = async () => {
      try {
        if (sessionStorage.getItem('mosaic_pending_greeting') !== '1') return;
        sessionStorage.removeItem('mosaic_pending_greeting');
        const res = await axios.get(`${API}/audio/mascot-greeting`);
        if (!res.data?.audio) return;
        const audio = new Audio(`data:audio/mp3;base64,${res.data.audio}`);
        audio.volume = 0.9;
        await audio.play().catch(() => {
          // Autoplay was blocked by the browser — restore the flag so
          // the next interaction-triggered mount can retry.
          try { sessionStorage.setItem('mosaic_pending_greeting', '1'); } catch (_e) {}
        });
      } catch (_e) {
        // Silent failure — greeting is nice-to-have, not blocking.
      }
    };
    playGreeting();

    const fetchData = async () => {
      try {
        const [profileRes, pausedRes, assignRes, feedbackRes] = await Promise.all([
          axios.get(`${API}/student/profile`),
          axios.get(`${API}/student/paused-exams`).catch(() => ({ data: { paused_sessions: [] } })),
          axios.get(`${API}/student/assignments`).catch(() => ({ data: { assignments: [] } })),
          axios.get(`${API}/student/feedback`).catch(() => ({ data: { feedback_items: [], summary: null } })),
        ]);
        setProfile(profileRes.data);
        setPausedExams(pausedRes.data.paused_sessions || pausedRes.data.paused_exams || []);
        setAssignments(assignRes.data.assignments || []);

        // Build WIDA levels from feedback
        const summary = feedbackRes.data.summary;
        if (summary && summary.average_score > 0) {
          const items = feedbackRes.data.feedback_items || [];
          const modalityScores = {};
          const modalityCounts = {};
          items.forEach(item => {
            const mod = item.modality;
            if (mod && item.score !== undefined) {
              modalityScores[mod] = (modalityScores[mod] || 0) + item.score;
              modalityCounts[mod] = (modalityCounts[mod] || 0) + 1;
            }
          });
          const levels = {};
          Object.keys(modalityScores).forEach(mod => {
            levels[mod] = scoreToWidaLevel(modalityScores[mod] / modalityCounts[mod]);
          });
          setMyLevels({
            modalities: levels,
            composite: scoreToWidaLevel(summary.average_score),
            strongest: summary.strongest_domain,
            weakest: summary.weakest_domain,
          });
        }
      } catch (err) {
        console.error('Error fetching student data:', err);
        setError('Unable to load your assessment. Please contact your teacher.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user, navigate]);

  const startExam = (grade) => {
    navigate(`/exam/setup/${grade}`);
  };

  // v6.36.18 — "Hear Moey" replay button. Plays a short, grade-band-
  // appropriate positive affirmation. Server picks a random line from
  // the student's pool, so repeated taps feel fresh.
  const [playingMoey, setPlayingMoey] = useState(false);
  const playMoeyAffirmation = async () => {
    if (playingMoey) return;
    setPlayingMoey(true);
    try {
      const res = await axios.get(`${API}/audio/moey-affirmation`);
      if (res.data?.audio) {
        const audio = new Audio(`data:audio/mp3;base64,${res.data.audio}`);
        audio.volume = 0.9;
        audio.onended = () => setPlayingMoey(false);
        audio.onerror = () => setPlayingMoey(false);
        await audio.play().catch(() => setPlayingMoey(false));
      } else {
        setPlayingMoey(false);
      }
    } catch (_e) {
      setPlayingMoey(false);
    }
  };

  const resumeExam = (examId) => {
    navigate(`/exam/${examId}?resume=true`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[70vh]">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-gray-500">Loading your assessment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[70vh]">
        <div className="text-center max-w-md">
          <GraduationCap size={48} className="mx-auto mb-4 text-gray-400" />
          <h2 className="text-xl font-bold mb-2">No Assessment Found</h2>
          <p className="text-gray-500 mb-6">{error}</p>
        </div>
      </div>
    );
  }

  const enrolledGrades = profile?.enrolled_grades || [];
  const classes = profile?.classes || [];
  const studentName = profile?.name || user?.name || 'Student';

  if (enrolledGrades.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[70vh]">
        <div className="text-center max-w-md">
          <GraduationCap size={48} className="mx-auto mb-4 text-gray-400" />
          <h2 className="text-xl font-bold mb-2">No Assessment Assigned</h2>
          <p className="text-gray-500 mb-6">
            You haven't been assigned to a class yet. Please contact your teacher to get enrolled.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-12" data-testid="my-assessment-page">
      {/* Welcome */}
      <div className="mb-10">
        <p className="text-sm text-gray-500 mb-2">Welcome back,</p>
        <h1 className="text-3xl sm:text-4xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="student-welcome">
          {studentName}
        </h1>
        <p className="text-gray-500 mb-4">
          {enrolledGrades.length === 1
            ? `Your assigned assessment is ready for Grade ${enrolledGrades[0]}.`
            : `You have assessments assigned for Grades ${enrolledGrades.join(', ')}.`}
        </p>
        {/* v6.36.18 — "Hear Moey" replay button. Short positive
            affirmation from the mascot, grade-appropriate. */}
        <button
          type="button"
          onClick={playMoeyAffirmation}
          disabled={playingMoey}
          className="inline-flex items-center gap-2 px-4 py-2 border-2 border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7] hover:text-white text-sm font-semibold rounded-full transition-colors disabled:opacity-60 disabled:cursor-wait"
          aria-label="Hear a short message from Moey"
          data-testid="hear-moey-btn"
        >
          <Volume2 size={16} className={playingMoey ? 'animate-pulse' : ''} />
          {playingMoey ? 'Moey is talking…' : 'Hear Moey'}
        </button>
      </div>

      {/* Can Do Progress Badges */}
      {myLevels && (
        <div className="mb-10 border-2 border-gray-100 p-6" data-testid="can-do-badges">
          <h3 className="font-bold text-sm text-gray-500 uppercase tracking-wide mb-4">My Proficiency Profile</h3>
          <div className="flex items-center gap-6 mb-4">
            <ProficiencyCompositeBar level={myLevels.composite} />
          </div>
          <div className="flex justify-center mb-5">
            <ProficiencyLevelRings levels={myLevels.modalities} size={52} />
          </div>
          {/* Can Do statements */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {['listening', 'speaking', 'reading', 'writing'].map(mod => {
              const level = myLevels.modalities[mod];
              if (!level) return null;
              const color = getWidaColor(level);
              const isStrongest = mod === myLevels.strongest;
              return (
                <div key={mod} className={`p-3 ${color.bg} border ${color.border} ${isStrongest ? 'ring-2 ring-offset-1 ring-green-400' : ''}`}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className={`text-xs font-bold ${color.text} uppercase`}>{mod}</span>
                    <span className={`text-[10px] ${color.text}`}>Level {level}</span>
                    {isStrongest && <span className="text-[10px] bg-green-200 text-green-800 px-1 font-semibold ml-auto">Strongest</span>}
                  </div>
                  <p className={`text-xs ${color.text} leading-relaxed`}>{getCanDo(mod, level)}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Paused Exams — Resume */}
      {pausedExams.length > 0 && (
        <div className="mb-8" data-testid="paused-exams-section">
          <h3 className="font-bold text-sm text-gray-500 uppercase tracking-wide mb-3">Resume In-Progress</h3>
          <div className="space-y-3">
            {pausedExams.map((exam) => (
              <div
                key={exam.id || exam.session_id}
                className="border-2 border-amber-300 bg-amber-50 p-5 flex items-center justify-between"
                data-testid={`paused-exam-${exam.id || exam.session_id}`}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-amber-100 flex items-center justify-center">
                    <Pause size={24} className="text-amber-700" />
                  </div>
                  <div>
                    <div className="font-bold">Grade {exam.grade_level || '?'} Assessment</div>
                    <div className="text-sm text-amber-700">
                      Paused &bull; {exam.time_elapsed ? `${Math.round(exam.time_elapsed / 60)} min elapsed` : 'In progress'}
                    </div>
                  </div>
                </div>
                <Button
                  onClick={() => resumeExam(exam.session_id || exam.id)}
                  className="btn-sharp bg-amber-600 hover:bg-amber-700 text-white h-11 px-6"
                  data-testid="resume-exam-btn"
                >
                  <Play size={16} className="mr-2" /> Resume
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Teacher/District Assigned Assessments */}
      {assignments.length > 0 && (
        <div className="mb-8" data-testid="teacher-assignments-section">
          <h3 className="font-bold text-sm text-gray-500 uppercase tracking-wide mb-3">
            <ClipboardList size={14} className="inline mr-1" />
            Assigned by Your Teacher
          </h3>
          <div className="space-y-3">
            {assignments.filter(a => a.my_status !== 'completed' && a.my_status !== 'scored').map(a => {
              const grade = a.grade_levels?.[0] || enrolledGrades[0];
              const isOverdue = a.due_date && new Date(a.due_date) < new Date();
              return (
                <div
                  key={a.id}
                  className={`border-2 ${isOverdue ? 'border-red-300 bg-red-50' : 'border-[#002FA7] bg-blue-50'} transition-colors`}
                  data-testid={`assigned-assessment-${a.id}`}
                >
                  <div className="p-5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 ${isOverdue ? 'bg-red-100' : 'bg-[#002FA7]'} ${isOverdue ? 'text-red-700' : 'text-white'} flex items-center justify-center font-bold text-lg`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                          {grade}
                        </div>
                        <div>
                          <h3 className="font-bold text-base">{a.title}</h3>
                          <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                            <span>Grades: {a.grade_levels?.join(', ')}</span>
                            <span>Form {a.form_id || 'A'}</span>
                            {a.due_date && (
                              <span className={`flex items-center gap-1 ${isOverdue ? 'text-red-600 font-semibold' : ''}`}>
                                <Calendar size={12} />
                                Due: {new Date(a.due_date).toLocaleDateString()}
                                {isOverdue && ' (Overdue)'}
                              </span>
                            )}
                          </div>
                          {a.notes && <p className="text-xs text-gray-500 mt-1 italic">{a.notes}</p>}
                        </div>
                      </div>
                      <Button
                        onClick={() => {
                          if (a.my_session_id && (a.my_status === 'in_progress' || a.my_status === 'paused')) {
                            navigate(`/exam/${a.my_session_id}?resume=true`);
                          } else {
                            startExam(grade);
                          }
                        }}
                        className={`btn-sharp h-11 px-6 ${isOverdue ? 'bg-red-600 hover:bg-red-700 text-white' : 'btn-sharp-primary'}`}
                        data-testid={`start-assigned-${a.id}`}
                      >
                        <Play size={16} className="mr-2" />
                        {a.my_status === 'in_progress' || a.my_status === 'paused' ? 'Resume' : 'Begin'}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Completed assignments */}
          {assignments.filter(a => a.my_status === 'completed' || a.my_status === 'scored').length > 0 && (
            <div className="mt-4">
              <p className="text-xs text-gray-400 mb-2">Completed</p>
              {assignments.filter(a => a.my_status === 'completed' || a.my_status === 'scored').map(a => (
                <div key={a.id} className="border border-green-200 bg-green-50 p-3 flex items-center gap-3 mb-2" data-testid={`completed-assignment-${a.id}`}>
                  <CheckCircle size={16} className="text-green-600 flex-shrink-0" />
                  <span className="text-sm text-green-800 font-medium">{a.title}</span>
                  <span className="text-xs text-green-600 ml-auto">Completed</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Assigned Assessments (from enrollment) */}
      <div data-testid="assigned-assessments">
        <h3 className="font-bold text-sm text-gray-500 uppercase tracking-wide mb-3">
          {pausedExams.length > 0 ? 'Start New Assessment' : 'Your Assessment'}
        </h3>
        <div className="space-y-4">
          {enrolledGrades.map((grade) => {
            const gradeClass = classes.find(c => c.grade_level === grade);
            return (
              <div
                key={grade}
                className="border-2 border-gray-200 hover:border-[#002FA7] transition-colors"
                data-testid={`assessment-card-${grade}`}
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-[#002FA7] text-white flex items-center justify-center font-bold text-xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {grade}
                      </div>
                      <div>
                        <h2 className="text-xl font-bold">Grade {grade} — MOSAIC Practice Assessment</h2>
                        {gradeClass && (
                          <p className="text-sm text-gray-500">{gradeClass.name}</p>
                        )}
                      </div>
                    </div>
                    <Button
                      onClick={() => startExam(grade)}
                      className="btn-sharp-primary h-12 px-8 text-base"
                      data-testid={`start-exam-btn-${grade}`}
                    >
                      <Play size={18} className="mr-2" /> Begin Assessment
                    </Button>
                  </div>

                  {/* Modality breakdown */}
                  <div className="grid grid-cols-4 gap-3 pt-4 border-t border-gray-100">
                    {[
                      { name: 'Listening', icon: Headphones, color: 'text-cyan-600', bg: 'bg-cyan-50' },
                      { name: 'Speaking', icon: Mic, color: 'text-purple-600', bg: 'bg-purple-50' },
                      { name: 'Reading', icon: BookOpen, color: 'text-blue-600', bg: 'bg-blue-50' },
                      { name: 'Writing', icon: PenTool, color: 'text-orange-600', bg: 'bg-orange-50' },
                    ].map(({ name, icon: Icon, color, bg }) => (
                      <div key={name} className={`flex items-center gap-2 p-2 ${bg}`}>
                        <Icon size={16} className={color} />
                        <span className="text-xs font-medium">{name}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Estimated time */}
                <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 flex items-center gap-4 text-sm text-gray-500">
                  <div className="flex items-center gap-1.5">
                    <Clock size={14} />
                    <span>~60 min</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <CheckCircle size={14} />
                    <span>63 questions</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Pause size={14} />
                    <span>You can pause anytime</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Help link */}
      <div className="mt-10 text-center">
        <Link to="/guide" className="text-sm text-gray-400 hover:text-[#002FA7] transition-colors">
          Need help? View the Student Guide
        </Link>
      </div>
    </div>
  );
};

export default MyAssessment;
